/**
 * Formatting Helpers
 *
 * Helper functions for formatting prices, dates, and other display values.
 * Uses Dutch locale (nl-NL) as default.
 */

import type { Price, ProductType } from '../types/orders';

/**
 * Format price using Dutch locale
 *
 * @param price - Price object with number and currency_code
 * @returns Formatted price string (e.g., "€ 50,00")
 */
export const formatPrice = (price: Price | null): string => {
  if (!price) return '';
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: price.currency_code,
  }).format(parseFloat(price.number));
};

/**
 * Format Unix timestamp to Dutch date format
 *
 * @param timestamp - Unix timestamp in seconds
 * @returns Formatted date string (e.g., "01-12-2024")
 */
export const formatDate = (timestamp: number | null): string => {
  if (!timestamp) return '';
  return new Date(timestamp * 1000).toLocaleDateString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/**
 * Formats a Dutch postcode for display.
 *
 * Postcodes are stored without a space (e.g. "3012KM") so they match the
 * backend / AllSolutions format, but the Dutch convention shows a space between
 * the four digits and the two letters ("3012 KM"). Values that don't match the
 * `1234AB` shape are returned unchanged.
 *
 * @param postalCode - The raw postcode (may be empty/undefined).
 * @returns The postcode with a space, or the input unchanged when it is not a
 *   four-digit + two-letter Dutch postcode.
 */
export const formatDutchPostalCode = (postalCode?: string | null): string => String(postalCode ?? '').replace(/^(\d{4})\s*([a-zA-Z]{2})$/, '$1 $2');

/**
 * Get a basic icon class for an order whose product has no image.
 *
 * Maps the product type to a representative icon so the orders overview can
 * show a sensible fallback for every order type (not just e-learnings).
 *
 * @param productType - The product type machine name from the orders API
 * @returns An icon font class name (without the leading `icon` base class)
 */
export const getProductFallbackIcon = (
  productType: ProductType | null,
): string => {
  switch (productType) {
    case 'fysiek_product':
      return 'icon-shopping-cart';
    case 'fysieke_training':
      return 'icon-notebook';
    case 'digital_tool':
      return 'icon-grid';
    case 'e_learning':
    default:
      return 'icon-book';
  }
};
